import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useStaticGlobalIntl } from 'utils/intl/useStaticGlobalIntl';
import { IconButton, Typography } from '@beeline.com/bp-design-system';
import { MixpanelEvents } from 'src/integrations/mixpanel/Events/MixpanelEvents';
import { useMixpanel } from 'src/integrations/mixpanel/useMixpanel';
export const DrawerContent: React.FC<
  React.PropsWithChildren<{
    title?: string | JSX.Element;
    subTitle?: string | JSX.Element;
    actions?: JSX.Element;
    headerAction?: JSX.Element;
    onClose?: () => void;
    maxWidth?: number | string;
    minWidth?: number | string;
    variant?: 'default' | 'noBorder';
    className?: string;
  }>
> = ({
  title,
  subTitle,
  onClose,
  maxWidth,
  minWidth,
  children,
  actions,
  headerAction,
  variant = 'default',
  className
}) => {
  const [closeTooltip] = useStaticGlobalIntl('common.drawer.button.close.tooltip');
  const { track } = useMixpanel();
  const onCloseHandler = useCallback(() => {
    onClose?.();
    track(MixpanelEvents.general.SIDE_PANEL_DRAWER_CLOSE_CLICK);
  }, [onClose, track]);
  return (
    <StyledPanel style={{ maxWidth, minWidth }} className={variant} $hasHeader={!!title} $hasActions={!!actions}>
      {title && (
        <StyledHeader className="header" $variant={variant}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography
              dataTest="side-drawer-header"
              variant={variant === 'noBorder' ? 'h2' : 'h3'}
              className="drawer-title"
            >
              {title}
            </Typography>
            {!!subTitle && <div style={{ marginTop: 4 }}>{subTitle}</div>}
          </div>
          {!!onClose && (
            <StyledHeaderActions className="actions" data-test="drawer-header-actions">
              {!!headerAction && headerAction}
              <IconButton
                dataTest="close-drawer-button"
                iconName="close"
                style={{ padding: 5 }}
                onClick={onCloseHandler}
                tooltipText={closeTooltip}
              />
            </StyledHeaderActions>
          )}
        </StyledHeader>
      )}
      <StyledContent className={className}>{children}</StyledContent>
      {!!actions && <StyledActions>{actions}</StyledActions>}
    </StyledPanel>
  );
};
const StyledHeaderActions = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: end;
`;
const StyledPanel = styled.div<{
  $hasHeader: boolean;
  $hasActions: boolean;
}>`
  background: ${({ theme }) => theme.colorSurface};
  color: ${({ theme }) => theme.colorOnSurface};
  min-width: 300px;
  height: 100%;
  display: grid;
  grid-template-rows: ${(props) => (props.$hasHeader ? '[header] auto' : '')} [content] 1fr ${(props) => (props.$hasActions ? '[actions] auto' : '')};
  grid-template-areas:
    'header'
    'content'
    'actions';
`;
const StyledContent = styled.div`
  grid-area: content;
  overflow: auto;
  height: 100%;
  padding: 1rem;
`;
const StyledActions = styled.div`
  grid-area: actions;
  align-self: end;
  padding: 1rem;
  text-align: right;
  border-top: 1px solid ${({ theme }) => theme.colorSurfaceVariant};
`;
const StyledHeader = styled.div<{
  $variant?: 'default' | 'noBorder';
}>`
  border-bottom: ${({ $variant, theme }) => ($variant === 'noBorder' ? 'none' : `1px solid ${theme.colorSurfaceVariant}`)};
  grid-area: header;
  display: grid;
  grid-template-columns: auto auto;
  padding: 1rem;
  align-self: start;
  .actions {
    text-align: right;
  }
`;
